import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import GooglePlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-google-places-autocomplete';
import { useAuth } from '@/contexts/AuthContext';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDucpiKqmlow-H0WUXZGDV8gbpd4qmf1Vk';

export interface AddAddressFormData {
  prefer_delivery: string;
  first_name: string;
  last_name: string;
  company_name: string;
  address1: string;
  address2: string;
  landmark: string;
  state: string;
  city: string;
  country: string;
  postcode: string;
  phone: string;
  address_type: 'home' | 'office';
  delivery_time: string;
  delivery_instructions: string;
}

interface AddAddressDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AddAddressFormData) => void;
  isLoading?: boolean;
}

export function AddAddressDrawer({ open, onOpenChange, onSubmit, isLoading }: AddAddressDrawerProps) {
  const { isAuthenticated, user, logout } = useAuth();

  const getInitialForm = (): AddAddressFormData => ({
    prefer_delivery: '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    company_name: '',
    address1: '',
    address2: '',
    landmark: '',
    state: '',
    city: '',
    country: '',
    postcode: '',
    phone: user?.phone || '',
    address_type: 'home',
    delivery_time: '7 am - 9 pm',
    delivery_instructions: '',
  });

  const [formData, setFormData] = useState<AddAddressFormData>(getInitialForm());
  const [addressValue, setAddressValue] = useState<any>(null);

  // Clear form when drawer closes. Also reset/prefill when opened (uses latest user).
  useEffect(() => {
    if (!open) {
      // Clear stored inputs when drawer is closed
      setFormData(getInitialForm());
      setAddressValue(null);
    } else {
      // Prefill when opened (use latest user data)
      setFormData(getInitialForm());
    }
    // re-run when open or user changes
  }, [open, user]);

  const handlePlaceSelect = async (place: any) => {
    
if (!place) {
    // ✅ when user clicks clear button
    console.log("Address cleared");
    setAddressValue(null);
    return;
  }
  if (!place) return;
    setAddressValue(place);

    try {
      // Get detailed address information
      const results = await geocodeByAddress(place.label);
      
      if (results && results.length > 0) {
        const addressComponents = results[0].address_components;
        const formattedAddress = results[0].formatted_address;

        // Get coordinates (optional - if you need lat/lng)
        // const { lat, lng } = await getLatLng(results[0]);

        let streetNumber = '';
        let route = '';
        let sublocality = '';
        let sublocalityLevel1 = '';
        let sublocalityLevel2 = '';
        let locality = '';
        let city = '';
        let state = '';
        let country = '';
        let postalCode = '';

        // Parse all address components
        addressComponents.forEach((component: any) => {
          const types = component.types;

          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (types.includes('route')) {
            route = component.long_name;
          }
          if (types.includes('sublocality_level_2')) {
            sublocalityLevel2 = component.long_name;
          }
          if (types.includes('sublocality_level_1')) {
            sublocalityLevel1 = component.long_name;
          }
          if (types.includes('sublocality')) {
            sublocality = component.long_name;
          }
          if (types.includes('locality')) {
            locality = component.long_name;
          }
          if (types.includes('administrative_area_level_2')) {
            if (!city) city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
          if (types.includes('country')) {
            country = component.long_name;
          }
          if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        });

        // Build street address
        const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
        
        // Determine area (prefer sublocality levels)
        const area = sublocalityLevel2 || sublocalityLevel1 || sublocality || '';
        
        // Determine city (prefer locality, fallback to administrative areas)
        const finalCity = locality || city || '';

        console.log('Selected place:', {
          formattedAddress,
          streetAddress,
          area,
          city: finalCity,
          state,
          country,
          postalCode
        });

        // Update form data with parsed address
        setFormData(prev => ({
          ...prev,
          address1: formattedAddress,
          address2: area,
          city: finalCity,
          state: state,
          country: country,
          postcode: postalCode,
        }));
      }
    } catch (error) {
      console.error('Error fetching address details:', error);
    }
  };

  const handleChange = (field: keyof AddAddressFormData, value: string) => {
   
    const time = value === "home" ? "7 am - 9 pm" : "10 am - 6 pm";
    setFormData(prev => ({ ...prev, 'delivery_time': time }));
    // form.setFieldsValue({
    //   deliveryTime: time,
    // });
    // setPreferTime(time);
    // delivery_time
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <div className="flex-shrink-0 px-6 pt-6">
          <SheetHeader>
            <SheetTitle className="text-xl">Add a New Address</SheetTitle>
          </SheetHeader>
          <p id="address-drawer-description" className="sr-only">
            Fill in the form to add a new delivery address
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0" aria-describedby="address-drawer-description">
          {/* Scrollable form content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  placeholder="Enter First Name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  placeholder="Enter Last Name"
                  required
                />
              </div>
            </div>

            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="Enter Company Name"
              />
            </div>

            {/* Location (Search in Google) */}
            <div className="space-y-2">
              <Label htmlFor="address1">
                Location (Search in Google) <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <GooglePlacesAutocomplete
                  apiKey={GOOGLE_MAPS_API_KEY}
                  selectProps={{
                    value: addressValue,
                    onChange: handlePlaceSelect,
                    placeholder: 'Search your address...',
                     isClearable: true,
                    styles: {
                      control: (provided) => ({
                        ...provided,
                        minHeight: '40px',
                        borderRadius: '6px',
                        borderColor: '#e5e7eb',
                        '&:hover': {
                          borderColor: '#d1d5db',
                        },
                      }),
                      input: (provided) => ({
                        ...provided,
                        fontSize: '14px',
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        fontSize: '14px',
                        color: '#9ca3af',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        fontSize: '14px',
                        backgroundColor: state.isSelected ? '#8b5cf6' : state.isFocused ? '#f3f4f6' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        cursor: 'pointer',
                      }),
                    },
                  }}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ['in'],
                    },
                  }}
                />
              </div>
              {/* Fallback manual input */}
              {/* <Input
                value={formData.address1}
                onChange={(e) => handleChange('address1', e.target.value)}
                placeholder="Or enter address manually"
                className="mt-2"
              /> */}
            </div>

            {/* Address2 */}
            <div className="space-y-2">
              <Label htmlFor="address2">
                Address2 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="address2"
                value={formData.address2}
                onChange={(e) => handleChange('address2', e.target.value)}
                placeholder="Enter Area, Street, Sector, Village"
                required
              />
            </div>

            {/* Landmark */}
            <div className="space-y-2">
              <Label htmlFor="landmark">Landmark</Label>
              <Input
                id="landmark"
                value={formData.landmark}
                onChange={(e) => handleChange('landmark', e.target.value)}
                placeholder="Enter Landmark"
              />
            </div>

            {/* State & City */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">
                  State <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="Enter State"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">
                  City <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Enter City"
                  required
                />
              </div>
            </div>

            {/* Country & Pincode */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">
                  Country <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="Enter Country"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">
                  Pincode <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => handleChange('postcode', e.target.value)}
                  placeholder="Enter Pincode"
                  required
                />
              </div>
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phone">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="please enter phone number"
                required
              />
            </div>

            {/* Select Preference */}
            <div className="space-y-2">
              <Label>Select Preference</Label>
              <RadioGroup
                value={formData.address_type}
                onValueChange={(value: 'home' | 'office') => handleChange('address_type', value)}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="home" id="home" />
                  <Label htmlFor="home" className="font-normal cursor-pointer">
                    Home
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="office" id="office" />
                  <Label htmlFor="office" className="font-normal cursor-pointer">
                    Office
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Preferable Delivery Time */}
            <div className="space-y-2">
              <Label htmlFor="delivery_time">Preferable Delivery Time</Label>
              <Input
                id="delivery_time"
                value={formData.delivery_time}
                onChange={(e) => handleChange('delivery_time', e.target.value)}
                placeholder="Preferable Delivery Time"
              />
            </div>

            {/* Delivery Instructions */}
            <div className="space-y-2">
              <Label htmlFor="delivery_instructions">Delivery Instructions</Label>
              <Input
                id="delivery_instructions"
                value={formData.delivery_instructions}
                onChange={(e) => handleChange('delivery_instructions', e.target.value)}
                placeholder="Delivery Instructions"
              />
            </div>
          </div>

          {/* Fixed Action Buttons */}
          <div className="flex-shrink-0 border-t bg-background px-6 py-4">
            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1"
                disabled={isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Address'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}